/* qeslint-disable */
// q@ts-nocheck

import {
    Cartesian2,
    Cartesian3,
    ClassificationType,
    Color,
    defined,
    Ellipsoid,
    HorizontalOrigin,
    LabelStyle,
    VerticalOrigin
} from "cesium";

const defaultLabelPixelOffset = new Cartesian2(0, -9);

export interface PointOptions {
    pixelSize?: number;
    color?: Color;
    position?: Cartesian3;
    disableDepthTestDistance?: number;
    show?: boolean;
}

export interface LabelOptions {
    show?: boolean;
    font?: string;
    outlineColor?: Color;
    outlineWidth?: number;
    style?: LabelStyle;
    text?: string;
    scale?: number;
    fillColor?: Color;
    showBackground?: boolean;
    backgroundColor?: Color;
    backgroundPadding?: Cartesian2;
    horizontalOrigin?: HorizontalOrigin;
    verticalOrigin?: VerticalOrigin;
    pixelOffset?: Cartesian2;
    disableDepthTestDistance?: number;
    position?: Cartesian3;
}

export interface PolylineOptions {
    show?: boolean;
    ellipsoid?: Ellipsoid;
    width?: number;
    color?: Color;
    depthTest?: boolean;
    depthFailColor?: Color;
    id?: string;
    positions?: Cartesian3[];
    materialType?: string;
    depthFailMaterialType?: string;
    loop?: boolean;
    clampToGround?: boolean;
    classificationType?: ClassificationType;
    allowPicking?: boolean;
}

export interface PolygonOptions {
    show?: boolean;
    ellipsoid?: Ellipsoid;
    width?: number;
    color?: Color;
    depthTest?: boolean;
    depthFailColor?: Color;
    id?: string;
    positions?: Cartesian3[];
    clampToGround?: boolean;
    classificationType?: ClassificationType;
    allowPicking?: boolean;
}

export class PrimitiveSettings {
    static color = Color.YELLOW;
    static labelFont = "16px Lucida Console";
    static textColor = Color.WHITE;
    static backgroundColor = new Color(0.165, 0.165, 0.165, 0.8);
    static backgroundPadding = new Cartesian2(7, 5);

    static getPolylineOptions(options: PolylineOptions = {}) {
        return {
            show: options.show ?? true,
            ellipsoid: options.ellipsoid,
            width: options.width ?? 3,
            color: options.color ?? PrimitiveSettings.color,
            depthTest: options.depthTest ?? false,
            depthFailColor: options.depthFailColor ?? options.color ?? PrimitiveSettings.color,
            id: options.id,
            positions: options.positions,
            materialType: options.materialType,
            depthFailMaterialType: options.depthFailMaterialType,
            loop: options.loop,
            clampToGround: options.clampToGround,
            classificationType: options.classificationType,
            allowPicking: options.allowPicking ?? true
        };
    }

    static getPolygonOptions(options: PolygonOptions = {}) {
        return {
            show: options.show ?? true,
            ellipsoid: options.ellipsoid,
            color: options.color ?? PrimitiveSettings.color,
            depthTest: options.depthTest ?? false,
            depthFailColor: options.depthFailColor ?? options.color ?? PrimitiveSettings.color,
            id: options.id,
            positions: options.positions,
            clampToGround: options.clampToGround,
            classificationType: options.classificationType,
            allowPicking: options.allowPicking ?? true
        };
    }

    static getPointOptions(options: PointOptions = {}) {
        return {
            pixelSize: options.pixelSize ?? 10,
            color: options.color ?? PrimitiveSettings.color,
            position: options.position ?? new Cartesian3(),
            disableDepthTestDistance: Number.POSITIVE_INFINITY, // for draw-over
            show: options.show ?? false
        };
    }

    static getLabelOptions(options: LabelOptions = {}) {
        const ret = {
            show: options.show ?? false,
            font: options.font ?? PrimitiveSettings.labelFont,
            style: options.style ?? LabelStyle.FILL,
            text: options.text ?? "",
            scale: options.scale ?? 1.0,
            fillColor: options.fillColor ?? PrimitiveSettings.textColor,
            showBackground: options.showBackground ?? true,
            backgroundColor: options.backgroundColor ?? PrimitiveSettings.backgroundColor,
            backgroundPadding: options.backgroundPadding ?? PrimitiveSettings.backgroundPadding,
            horizontalOrigin: options.horizontalOrigin ?? HorizontalOrigin.CENTER,
            verticalOrigin: options.verticalOrigin ?? VerticalOrigin.BOTTOM,
            pixelOffset: defined(options.pixelOffset) ? options.pixelOffset : Cartesian2.clone(defaultLabelPixelOffset),
            disableDepthTestDistance: options.disableDepthTestDistance ?? Number.POSITIVE_INFINITY, // for draw-over
            position: options.position ?? new Cartesian3()
        };

        if (ret.style === LabelStyle.OUTLINE || ret.style === LabelStyle.FILL_AND_OUTLINE) {
            options.outlineColor = options.outlineColor ?? Color.BLACK;
            options.outlineWidth = options.outlineWidth ?? 1;
        }

        return ret;
    }
}
